<script setup lang="ts">
import { useAiChat } from "ls/api/cloud/ai";
import TextArea from "ls/components/form/TextArea.vue";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { CornerDownLeftIcon } from "lucide-vue-next";
import { ref } from "vue";
import { parseMarkdownLine } from "../disko/rich-text/streaming-markdown";

const props = defineProps<{
  chatId: string;
  placeholder?: string;
  currentMessageId?: string;
}>();

const emit = defineEmits<{
  maximize: [];
}>();

const input = ref("");

const conversation = useAiChat(props.chatId);
async function submit() {
  const inputValue = input.value.trim();
  if (!inputValue) {
    return;
  }

  if (conversation.messages.length === 0 || (conversation.messages.length === 1 && conversation.messages[0].isIncoming)) {
    return;
  }

  const lines = inputValue.split("\n");
  const content = lines.map(line => ({
    index: 0,
    text: line,
    markdown: parseMarkdownLine(line),
  }));
  conversation.messages.push({
    role: "user",
    content,
    inputTokenCount: 0,
    outputTokenCount: 0,
    cachedTokenCount: 0,
  });
  input.value = "";

  if (!conversation.hasStarted) {
    await conversation.startConversation("chat");
  } else {
    await conversation.submitConversation(props.currentMessageId);
  }
}

function onTextAreaEnterKeydown(e: KeyboardEvent) {
  if (e.shiftKey) {
    // Shift+Enter → allow the newline (do not preventDefault)
    return;
  }

  if (e.altKey) {
    e.preventDefault();
    emit("maximize");
    return;
  }

  // Plain Enter → prevent the newline and submit instead
  e.preventDefault();
  submit();
}
</script>

<template>
  <div class="relative flex min-h-0 w-full flex-1">
    <TextArea
      v-model="input"
      autofocus
      rows="1"
      size="auto"
      autoresize
      :placeholder="placeholder"
      class="resize-none overflow-auto px-4 py-3 pr-10"
      @keydown.enter="onTextAreaEnterKeydown"
    />

    <div class="absolute inset-y-0 end-0 flex items-end p-2">
      <SimpleButton
        variant="primary"
        size="icon"
        @click="submit"
      >
        <CornerDownLeftIcon class="size-4 flex-none" aria-hidden="true" />
      </SimpleButton>
    </div>
  </div>
</template>
